import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import { rxResource } from "@angular/core/rxjs-interop";
import { catchError, forkJoin, map, of, switchMap } from "rxjs";
import { BaseChartDirective } from "ng2-charts";
import type { ChartData, ChartOptions } from "chart.js";
import { ApiService } from "../core/api.service";
import { CustomerSummary, Estimate, Order } from "../core/models";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Statuses considered "in production" for the stalled-job rule. */
const PRODUCTION_STATUSES: readonly string[] = [
  "In production",
  "Delivered",
  "Installed",
  "Complete paid",
];

interface ActionItem {
  kind: string;
  title: string;
  subtitle: string;
  link: string[];
}

@Component({
  selector: "app-dashboard",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterModule, CommonModule, BaseChartDirective],
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
})
export class DashboardComponent {
  private readonly api = inject(ApiService);

  private readonly dataResource = rxResource({
    stream: () =>
      this.api.getCustomerSummaries().pipe(
        catchError(() => of([] as CustomerSummary[])),
        switchMap((customers) => {
          if (customers.length === 0) {
            return of({ customers, estimates: [] as (Estimate | null)[] });
          }
          return forkJoin(
            customers.map((c) =>
              this.api.getEstimate(c.id).pipe(catchError(() => of(null))),
            ),
          ).pipe(map((estimates) => ({ customers, estimates })));
        }),
      ),
  });

  private readonly ordersResource = rxResource({
    stream: () =>
      this.api.getOrders().pipe(catchError(() => of([] as Order[]))),
  });

  readonly customers = computed(
    () => this.dataResource.value()?.customers ?? [],
  );
  private readonly estimates = computed(
    () => this.dataResource.value()?.estimates ?? [],
  );
  private readonly orders = computed(() => this.ordersResource.value() ?? []);
  readonly loading = computed(
    () => this.dataResource.isLoading() || this.ordersResource.isLoading(),
  );

  // ── KPI cards ──────────────────────────────────────────────────────────────
  readonly kpis = computed(() => {
    const now = Date.now();
    const since7 = now - 7 * DAY_MS;
    const orders = this.orders();
    const customers = this.customers();

    return {
      newOrders: orders.filter((o) => this.time(o.createdAt) >= since7).length,
      newJobs: customers.filter((c) => this.time(c.createdAt) >= since7).length,
      inProgress: customers.filter(
        (c) =>
          c.status !== "Installed" &&
          c.status !== "Complete paid" &&
          c.roomsCount > 0,
      ).length,
      awaitingQuote: customers.filter((c) =>
        Object.values(c.supplierQuoteStatuses ?? {}).includes("submitted"),
      ).length,
    };
  });

  // ── Charts ─────────────────────────────────────────────────────────────────
  private readonly months = this.last12Months();
  private readonly monthLabels = this.months.map((d) =>
    d.toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
  );

  readonly ordersChart = computed<ChartData<"line">>(() => {
    const admin = new Map(this.months.map((d) => [this.monthKey(d), 0]));
    const diy = new Map(this.months.map((d) => [this.monthKey(d), 0]));
    for (const customer of this.customers()) {
      const key = this.monthKey(customer.createdAt);
      if (admin.has(key)) admin.set(key, (admin.get(key) ?? 0) + 1);
    }
    for (const order of this.orders()) {
      const key = this.monthKey(order.createdAt);
      if (diy.has(key)) diy.set(key, (diy.get(key) ?? 0) + 1);
    }
    return {
      labels: this.monthLabels,
      datasets: [
        {
          label: "Admin jobs",
          data: this.months.map((d) => admin.get(this.monthKey(d)) ?? 0),
          borderColor: "#6366f1",
          backgroundColor: "rgba(99, 102, 241, 0.12)",
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          pointBackgroundColor: "#6366f1",
        },
        {
          label: "DIY orders",
          data: this.months.map((d) => diy.get(this.monthKey(d)) ?? 0),
          borderColor: "#10b981",
          backgroundColor: "rgba(16, 185, 129, 0.12)",
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          pointBackgroundColor: "#10b981",
        },
      ],
    };
  });

  readonly statusChart = computed<ChartData<"doughnut">>(() => {
    const counts = new Map<string, number>();
    for (const customer of this.customers()) {
      const status = customer.status || "Unknown";
      counts.set(status, (counts.get(status) ?? 0) + 1);
    }
    return {
      labels: [...counts.keys()],
      datasets: [
        {
          data: [...counts.values()],
          backgroundColor: [
            "#6366f1",
            "#10b981",
            "#f59e0b",
            "#ef4444",
            "#8b5cf6",
            "#06b6d4",
            "#f43f5e",
            "#84cc16",
          ],
          borderWidth: 0,
        },
      ],
    };
  });

  readonly ordersOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: true, position: "bottom" } },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  readonly doughnutOptions: ChartOptions<"doughnut"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "right" } },
  };

  // ── Needs action queue ─────────────────────────────────────────────────────
  readonly needsAction = computed<ActionItem[]>(() => {
    const items: ActionItem[] = [];
    const cutoff = Date.now() - 14 * DAY_MS;
    const estimates = this.estimates();

    for (const customer of this.customers()) {
      if (
        Object.values(customer.supplierQuoteStatuses ?? {}).includes("submitted")
      ) {
        items.push({
          kind: "Quote",
          title: customer.name,
          subtitle: "Quote awaiting review",
          link: ["/customers", customer.id, "estimate"],
        });
      }
    }

    for (const order of this.orders()) {
      if (order.paymentStatus === "paid" && !order.convertedToCustomerId) {
        items.push({
          kind: "Order",
          title: order.customerName || order.id,
          subtitle: "Import to production",
          link: ["/orders"],
        });
      }
    }

    // Customer records carry no updatedAt, so use the estimate's (the record
    // that changes during production), falling back to the job's createdAt.
    const lastActivity = new Map<string, string>();
    for (const estimate of estimates) {
      if (estimate) lastActivity.set(estimate.customerId, estimate.updatedAt);
    }

    for (const customer of this.customers()) {
      if (!customer.status || !PRODUCTION_STATUSES.includes(customer.status)) {
        continue;
      }
      const explicitUpdatedAt = (
        customer as CustomerSummary & { updatedAt?: string }
      ).updatedAt;
      const last =
        explicitUpdatedAt || lastActivity.get(customer.id) || customer.createdAt;
      if (this.time(last) < cutoff) {
        items.push({
          kind: "Stalled",
          title: customer.name,
          subtitle: `Stalled 14d+ in ${customer.status}`,
          link: ["/customers", customer.id],
        });
      }
    }

    return items;
  });

  // ── Helpers ────────────────────────────────────────────────────────────────
  private time(value?: string): number {
    const ms = value ? new Date(value).getTime() : NaN;
    return Number.isNaN(ms) ? 0 : ms;
  }

  private monthKey(value: string | Date): string {
    const d = typeof value === "string" ? new Date(value) : value;
    return `${d.getFullYear()}-${d.getMonth()}`;
  }

  private last12Months(): Date[] {
    const months: Date[] = [];
    const today = new Date();
    for (let i = 11; i >= 0; i--) {
      months.push(new Date(today.getFullYear(), today.getMonth() - i, 1));
    }
    return months;
  }
}
